// Rich, searchable service dropdown — each option shows the service image
// (or themed fallback), name, one-line description and duration/price chips.

import { useMemo, useState } from 'react';
import { CalendarClock, ChevronDown, Check, Clock, Search } from 'lucide-react';
import clsx from 'clsx';
import type { ApptService } from '@/services/appointments.service';
import { Popover } from './Popover';
import { useCompanyCurrency } from '@/contexts/WorkspaceContext';

const Thumb = ({ s, size = 38 }: { s?: ApptService | null; size?: number }) =>
  s?.imageUrl ? (
    <img src={s.imageUrl} alt="" style={{ width: size, height: size }} className="rounded-lg object-cover object-top border border-border shrink-0" />
  ) : (
    <span style={{ width: size, height: size }} className="rounded-lg bg-primary-soft text-primary flex items-center justify-center shrink-0">
      <CalendarClock size={size * 0.45} strokeWidth={1.6} />
    </span>
  );

interface Props {
  services: ApptService[];
  value: string;                    // serviceUid
  onChange: (serviceUid: string) => void;
  disabled?: boolean;
  className?: string;
  error?: boolean;
}

const ServicePicker = ({ services, value, onChange, disabled, className, error }: Props) => {
  const companyCurrency = useCompanyCurrency();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selected = services.find((s) => s.serviceUid === value) ?? null;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return services.filter((s) => !q
      || s.name.toLowerCase().includes(q)
      || (s.description || '').toLowerCase().includes(q));
  }, [services, search]);

  return (
    <Popover
      open={open}
      onOpenChange={(o) => { if (!disabled) { setOpen(o); if (!o) setSearch(''); } }}
      className={className}
      panelClassName="w-full min-w-full"
      trigger={
        <button
          type="button"
          disabled={disabled}
          onClick={() => { if (!disabled) setOpen((o) => !o); }}
          className={clsx(
            'w-full min-h-[52px] rounded-xl border bg-surface px-3 py-2 text-left',
            'flex items-center gap-3 transition-colors',
            error ? 'border-danger' : 'border-border',
            disabled ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer hover:border-primary/50',
          )}
        >
          <Thumb s={selected} />
          <span className="min-w-0 flex-1">
            {selected ? (
              <>
                <span className="block text-[13.5px] font-semibold text-text-primary truncate">{selected.name}</span>
                <span className="block text-[11px] text-text-muted truncate">
                  {selected.durationMinutes} min{selected.price != null ? ` · ${selected.currency || companyCurrency} ${selected.price}` : ''}
                </span>
              </>
            ) : (
              <span className="text-[13.5px] text-text-muted">Pick a service…</span>
            )}
          </span>
          {!disabled && <ChevronDown size={15} className="text-text-muted shrink-0" />}
        </button>
      }
    >
      {/* search */}
      <div className="p-1.5 border-b border-border">
        <div className="flex items-center gap-2 rounded-lg bg-surface-alt px-2.5 h-9 transition-colors">
          <Search className="h-3.5 w-3.5 shrink-0 text-text-muted" />
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search services…"
            className="h-full w-full bg-transparent text-sm text-text-primary outline-none placeholder:text-text-muted"
          />
        </div>
      </div>
      <div className="max-h-[280px] overflow-y-auto flex flex-col gap-1 p-1.5">
        {filtered.map((s) => {
          const sel = s.serviceUid === value;
          return (
            <button
              key={s.serviceUid}
              type="button"
              onClick={() => { onChange(s.serviceUid); setOpen(false); }}
              className={clsx(
                'flex items-start gap-3 w-full rounded-lg px-2.5 py-2 border-none text-left transition-colors cursor-pointer',
                sel ? 'bg-primary-soft' : 'bg-transparent hover:bg-surface-alt',
              )}
            >
              <Thumb s={s} size={42} />
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5">
                  <span className={clsx('text-[13px] font-semibold truncate', sel ? 'text-primary' : 'text-text-primary')}>{s.name}</span>
                  {sel && <Check size={13} className="text-primary shrink-0" />}
                </span>
                <span className="block text-[11px] text-text-muted mt-[1px] truncate">{s.description || 'No description'}</span>
                <span className="flex items-center gap-1.5 mt-1">
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-surface-alt rounded-full px-2 py-[2px] text-text-primary"><Clock size={9} /> {s.durationMinutes} min</span>
                  {s.price != null && (
                    <span className="inline-flex items-center text-[10px] font-semibold bg-primary-soft text-primary rounded-full px-2 py-[2px]">{s.currency || companyCurrency} {s.price}</span>
                  )}
                </span>
              </span>
            </button>
          );
        })}
        {filtered.length === 0 && (
          <div className="py-6 text-center text-[12px] text-text-muted">
            {services.length === 0 ? 'No active services.' : 'No services match your search.'}
          </div>
        )}
      </div>
    </Popover>
  );
};

export default ServicePicker;
